import { DT } from '../../theme/designTokens';
import { PANEL_LABEL, TEAM_A_COLOR, TEAM_B_COLOR } from './MatchCard';
import { CountryFlag } from '../ui/CountryFlag';
import { PlayerAvatar } from '../ui/PlayerAvatar';
import { useTranslation } from '../../i18n/react';

/**
 * PlayersCard — the PLAYERS section of the game info panel, per the GAMESCENE
 * Figma: a gold-bordered card with two columns (Team A / Team B).
 *
 * Player Identity V1: rows carry the authoritative identity from the game
 * state (via the GameScene → React `phaser:players-update` event) and render
 * the approved arrangement — [flag] [avatar] [full name] — through the
 * shared primitives. Bots never show a flag; humans with no country show the
 * neutral globe; no avatar_key → display-name initial. Until the first
 * players event arrives (or on an older backend), the generic placeholder
 * rows render unchanged.
 *
 * Reused by both desktop and mobile layouts.
 */

export interface PlayerRow {
  label:  string;
  isMe?:  boolean;
  isBot?: boolean;
  /** Raw country code from the payload — CountryFlag validates it. */
  countryCode?: string | null;
  /** Raw avatar key from the payload — PlayerAvatar validates it. */
  avatarKey?: string | null;
  /** True when this row carries a real resolved player identity (flags/avatars
   *  render only then — never on placeholder labels like "Opponent"). */
  resolved?: boolean;
  /** True when this row can be friend-requested (opponent, human, not yet friend). */
  canAdd?: boolean;
  /** This row's user id — required to actually send a game-context friend request. */
  userId?: number;
  /** True once a friend request has been sent this session — shows a disabled "Requested" label instead of the add button. */
  addSent?: boolean;
}

const CARD: React.CSSProperties = {
  boxSizing:    'border-box',
  background:   DT.color.cardBg,
  border:       `1px solid ${DT.color.gold}`,
  borderRadius: 16,
  padding:      '16px 18px',
};

interface Props {
  teamA:  PlayerRow[];
  teamB:  PlayerRow[];
  onAdd?: (row: PlayerRow) => void;
}

export function PlayersCard({ teamA, teamB, onAdd }: Props) {
  const { t } = useTranslation('gameplay');
  return (
    <div>
      <div style={{ ...PANEL_LABEL, marginBottom: 12 }}>{t('players.heading')}</div>

      <div style={{ ...CARD, display: 'flex', gap: 16 }}>
        <TeamColumn label={t('team.a')} color={TEAM_A_COLOR} rows={teamA} onAdd={onAdd} />
        <TeamColumn label={t('team.b')} color={TEAM_B_COLOR} rows={teamB} onAdd={onAdd} />
      </div>
    </div>
  );
}

function TeamColumn({
  label, color, rows, onAdd,
}: { label: string; color: string; rows: PlayerRow[]; onAdd?: (r: PlayerRow) => void }) {
  const { t } = useTranslation('gameplay');
  return (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ fontFamily: DT.font.sans, fontSize: 14, color, marginBottom: 2 }}>{label}</div>
      {rows.map((row, i) => {
        const badge = row.canAdd && row.addSent
          ? (
            <span style={{ whiteSpace: 'nowrap', fontFamily: DT.font.sans, fontSize: 12, color: DT.color.textMuted }}>
              {t('players.requested')}
            </span>
          )
          : row.canAdd
            ? (
              <button
                onClick={() => onAdd?.(row)}
                style={{
                  whiteSpace: 'nowrap', background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: DT.font.sans, fontSize: 12, color: DT.color.gold,
                }}
              >
                {t('players.add')}
              </button>
            )
            : null;

        return (
          // flexWrap here is the fix: the identity group (flag/avatar/name)
          // and the add/Requested badge never shrink against EACH OTHER
          // (both flexShrink: 0) — if they don't both fit on one line, the
          // badge wraps to its own line below instead of squeezing the name
          // down to an unreadable sliver. The name text still truncates on
          // its own, but only against the row's full width, never against
          // the badge's width.
          <div key={i} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 6, rowGap: 2, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, maxWidth: '100%', minWidth: 0 }}>
              {/* Identity renders ONLY for real resolved players — generic
                  placeholder labels get no flag slot and no avatar. */}
              {row.resolved && !row.isBot && (
                <CountryFlag countryCode={row.countryCode} width={20} height={14} decorative loading="eager" />
              )}
              {row.resolved && (
                <PlayerAvatar avatarKey={row.avatarKey} name={row.label} size={20} loading="eager" />
              )}
              <span
                title={row.resolved ? row.label : undefined}
                style={{
                  minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: DT.font.sans, fontStyle: 'italic', fontSize: 14,
                  color: DT.color.textPrimary, fontWeight: row.isMe ? 700 : 400,
                }}
              >
                {row.label}
              </span>
            </div>

            {badge && <div style={{ flexShrink: 0 }}>{badge}</div>}
          </div>
        );
      })}
      {rows.length === 0 && <span style={{ fontFamily: DT.font.sans, fontSize: 13, color: DT.color.textMuted }}>—</span>}
    </div>
  );
}
